import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { prescriptionAPI } from '../../lib/api';
import { 
  Pill, 
  Clock, 
  CheckCircle,
  Warning,
  X,
  PaperPlaneTilt,
  Storefront,
  Phone,
  EnvelopeSimple,
  MapPin
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function PrescriptionsPage() {
  const { user } = useAuth();
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPrescription, setSelectedPrescription] = useState(null);
  const [pharmacyModalOpen, setPharmacyModalOpen] = useState(false);
  const [sendingToPharmacy, setSendingToPharmacy] = useState(false);
  const [pharmacyData, setPharmacyData] = useState({
    pharmacy_name: '',
    pharmacy_email: '',
    pharmacy_fax: '',
    pharmacy_phone: '',
    pharmacy_address: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await prescriptionAPI.getAll();
      setPrescriptions(res.data);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error loading prescriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active': return 'status-in-progress';
      case 'completed': return 'status-completed';
      case 'cancelled': return 'bg-red-100 text-red-700';
      case 'on_hold': return 'status-pending';
      default: return 'status-pending';
    }
  };

  const openPharmacyModal = (prescription) => {
    setSelectedPrescription(prescription);
    // Pre-fill pharmacy info if already set
    setPharmacyData({
      pharmacy_name: prescription.pharmacy || '',
      pharmacy_email: prescription.pharmacy_email || '',
      pharmacy_fax: prescription.pharmacy_fax || '',
      pharmacy_phone: '',
      pharmacy_address: '',
      notes: ''
    });
    setPharmacyModalOpen(true);
  };

  const handleSendToPharmacy = async () => {
    if (!selectedPrescription || !pharmacyData.pharmacy_name) {
      toast.error('Please enter pharmacy name');
      return;
    }

    if (!pharmacyData.pharmacy_email && !pharmacyData.pharmacy_fax) {
      toast.error('Please enter pharmacy email or fax number');
      return;
    }

    setSendingToPharmacy(true);
    try {
      const response = await prescriptionAPI.sendToPharmacy(selectedPrescription.id, pharmacyData);
      
      toast.success(`Prescription sent to ${response.data.pharmacy_name}`, {
        description: `Mode: ${response.data.send_mode}`
      });
      
      // Update local state
      setPrescriptions(prev => prev.map(p => 
        p.id === selectedPrescription.id 
          ? { ...p, pharmacy: pharmacyData.pharmacy_name, sent_to_pharmacy: true }
          : p
      ));
      
      setPharmacyModalOpen(false);
      setSelectedPrescription(null);
      setPharmacyData({
        pharmacy_name: '',
        pharmacy_email: '',
        pharmacy_fax: '',
        pharmacy_phone: '',
        pharmacy_address: '',
        notes: ''
      });
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Send to pharmacy error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send prescription');
    } finally {
      setSendingToPharmacy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  const activePrescriptions = prescriptions.filter(p => p.status === 'active');
  const otherPrescriptions = prescriptions.filter(p => p.status !== 'active');

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
          {user?.role === 'patient' ? 'My Prescriptions' : 'Prescriptions'}
        </h1>
        <p className="text-slate-500 mt-1">
          {user?.role === 'patient' ? 'View your current medications' : 'Manage patient prescriptions'}
        </p>
      </div>

      {/* Active Prescriptions */}
      <div className="card-clinical mb-6">
        <div className="p-6 border-b border-slate-200">
          <h2 className="font-semibold text-slate-900 flex items-center gap-2">
            <Pill size={18} className="text-teal-600" /> Active Prescriptions
          </h2>
        </div>
        
        {activePrescriptions.length === 0 ? (
          <div className="p-12 text-center">
            <Pill size={48} className="text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No active prescriptions</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {activePrescriptions.map((rx) => (
              <div 
                key={rx.id} 
                className="p-4 hover:bg-slate-50"
                data-testid={`prescription-${rx.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex gap-4 cursor-pointer" onClick={() => setSelectedPrescription(rx)}>
                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                      <Pill size={20} className="text-teal-600" />
                    </div>
                    <div>
                      <div className="font-medium text-slate-900">{rx.medication_name}</div>
                      <div className="text-sm text-slate-600">{rx.dosage} - {rx.frequency}</div>
                      <div className="text-sm text-slate-500 mt-1">
                        {user?.role === 'patient' ? `Prescribed by: ${rx.provider_name}` : `Patient: ${rx.patient_name}`}
                      </div>
                      {rx.sent_to_pharmacy && (
                        <div className="flex items-center gap-1 text-xs text-green-600 mt-1">
                          <CheckCircle size={12} weight="fill" />
                          Sent to {rx.pharmacy || 'pharmacy'}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="text-right">
                      <span className={`status-badge ${getStatusBadge(rx.status)}`}>
                        {rx.status}
                      </span>
                      <div className="text-xs text-slate-500 mt-2">
                        {rx.refills} refills remaining
                      </div>
                    </div>
                    {/* Send to Pharmacy Button - Staff Only */}
                    {user?.role !== 'patient' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openPharmacyModal(rx);
                        }}
                        className="btn-teal text-sm flex items-center gap-1"
                        data-testid={`send-pharmacy-${rx.id}`}
                        title="Send to Pharmacy"
                      >
                        <Storefront size={16} />
                        Send to Pharmacy
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Past Prescriptions */}
      {otherPrescriptions.length > 0 && (
        <div className="card-clinical">
          <div className="p-6 border-b border-slate-200">
            <h2 className="font-semibold text-slate-900">Past Prescriptions</h2>
          </div>
          <div className="divide-y divide-slate-100">
            {otherPrescriptions.slice(0, 10).map((rx) => (
              <div 
                key={rx.id} 
                className="p-4 hover:bg-slate-50 cursor-pointer"
                onClick={() => setSelectedPrescription(rx)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
                      <Pill size={16} className="text-slate-400" />
                    </div>
                    <div>
                      <div className="font-medium text-slate-700">{rx.medication_name}</div>
                      <div className="text-sm text-slate-500">{rx.dosage}</div>
                    </div>
                  </div>
                  <span className={`status-badge ${getStatusBadge(rx.status)}`}>
                    {rx.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Prescription Detail Modal */}
      {selectedPrescription && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900">Prescription Details</h2>
                <button onClick={() => setSelectedPrescription(null)} className="text-slate-400 hover:text-slate-600">
                  <X size={20} />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                  <Pill size={24} className="text-teal-600" />
                </div>
                <div>
                  <div className="font-semibold text-lg text-slate-900">{selectedPrescription.medication_name}</div>
                  <span className={`status-badge ${getStatusBadge(selectedPrescription.status)}`}>
                    {selectedPrescription.status}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4">
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider">Dosage</div>
                  <div className="font-medium text-slate-900">{selectedPrescription.dosage}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider">Frequency</div>
                  <div className="font-medium text-slate-900">{selectedPrescription.frequency}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider">Duration</div>
                  <div className="font-medium text-slate-900">{selectedPrescription.duration}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider">Quantity</div>
                  <div className="font-medium text-slate-900">{selectedPrescription.quantity}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider">Refills</div>
                  <div className="font-medium text-slate-900">{selectedPrescription.refills}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider">Prescribed</div>
                  <div className="font-medium text-slate-900">
                    {new Date(selectedPrescription.prescribed_date).toLocaleDateString()}
                  </div>
                </div>
              </div>

              {selectedPrescription.instructions && (
                <div className="pt-4 border-t border-slate-200">
                  <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Instructions</div>
                  <p className="text-slate-700">{selectedPrescription.instructions}</p>
                </div>
              )}

              {selectedPrescription.pharmacy && (
                <div className="pt-4 border-t border-slate-200">
                  <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Pharmacy</div>
                  <p className="text-slate-700">{selectedPrescription.pharmacy}</p>
                  {selectedPrescription.sent_to_pharmacy && (
                    <div className="flex items-center gap-1 text-xs text-green-600 mt-1">
                      <CheckCircle size={12} weight="fill" />
                      Sent to pharmacy
                    </div>
                  )}
                </div>
              )}

              <div className="pt-4 border-t border-slate-200">
                <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Provider</div>
                <p className="text-slate-700">{selectedPrescription.provider_name}</p>
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 space-y-3">
              {user?.role !== 'patient' && (
                <button 
                  onClick={() => {
                    setSelectedPrescription(null);
                    openPharmacyModal(selectedPrescription);
                  }}
                  className="btn-teal w-full flex items-center justify-center gap-2"
                  data-testid="detail-send-pharmacy-btn"
                >
                  <Storefront size={18} />
                  Send to Pharmacy
                </button>
              )}
              <button onClick={() => setSelectedPrescription(null)} className="btn-outline w-full">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send to Pharmacy Modal */}
      {pharmacyModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Send to Pharmacy</h3>
                <p className="text-sm text-slate-500 mt-1">
                  {selectedPrescription?.medication_name} - {selectedPrescription?.dosage}
                </p>
              </div>
              <button
                onClick={() => {
                  setPharmacyModalOpen(false);
                  setPharmacyData({
                    pharmacy_name: '',
                    pharmacy_email: '',
                    pharmacy_fax: '',
                    pharmacy_phone: '',
                    pharmacy_address: '',
                    notes: ''
                  });
                }}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="p-3 bg-teal-50 rounded-lg border border-teal-200">
                <div className="flex items-center gap-2 text-teal-800">
                  <Pill size={18} />
                  <span className="font-medium">{selectedPrescription?.medication_name}</span>
                </div>
                <p className="text-sm text-teal-700 mt-1">
                  {selectedPrescription?.dosage} • {selectedPrescription?.frequency} • Qty: {selectedPrescription?.quantity}
                </p>
                <p className="text-sm text-teal-600 mt-1">
                  Patient: {selectedPrescription?.patient_name}
                </p>
              </div>
              
              <div>
                <label className="label-clinical flex items-center gap-2">
                  <Storefront size={14} className="text-slate-500" />
                  Pharmacy Name *
                </label>
                <input
                  type="text"
                  value={pharmacyData.pharmacy_name}
                  onChange={(e) => setPharmacyData(prev => ({ ...prev, pharmacy_name: e.target.value }))}
                  placeholder="CVS Pharmacy, Walgreens, etc."
                  className="input-clinical"
                  data-testid="pharmacy-name-input"
                  required
                />
              </div>
              
              <div>
                <label className="label-clinical flex items-center gap-2">
                  <EnvelopeSimple size={14} className="text-slate-500" />
                  Pharmacy Email
                </label>
                <input
                  type="email"
                  value={pharmacyData.pharmacy_email}
                  onChange={(e) => setPharmacyData(prev => ({ ...prev, pharmacy_email: e.target.value }))}
                  placeholder="pharmacy@example.com"
                  className="input-clinical"
                  data-testid="pharmacy-email-input"
                />
              </div>
              
              <div>
                <label className="label-clinical flex items-center gap-2">
                  <Phone size={14} className="text-slate-500" />
                  Pharmacy Fax
                </label>
                <input
                  type="text"
                  value={pharmacyData.pharmacy_fax}
                  onChange={(e) => setPharmacyData(prev => ({ ...prev, pharmacy_fax: e.target.value }))}
                  placeholder="(555) 123-4567"
                  className="input-clinical"
                  data-testid="pharmacy-fax-input"
                />
                <p className="text-xs text-slate-500 mt-1">Enter email OR fax number</p>
              </div>
              
              <div>
                <label className="label-clinical flex items-center gap-2">
                  <Phone size={14} className="text-slate-500" />
                  Pharmacy Phone (Optional)
                </label>
                <input
                  type="text"
                  value={pharmacyData.pharmacy_phone}
                  onChange={(e) => setPharmacyData(prev => ({ ...prev, pharmacy_phone: e.target.value }))}
                  placeholder="(555) 123-4567"
                  className="input-clinical"
                />
              </div>
              
              <div>
                <label className="label-clinical flex items-center gap-2">
                  <MapPin size={14} className="text-slate-500" />
                  Pharmacy Address (Optional)
                </label>
                <input
                  type="text"
                  value={pharmacyData.pharmacy_address}
                  onChange={(e) => setPharmacyData(prev => ({ ...prev, pharmacy_address: e.target.value }))}
                  placeholder="123 Main St, City, State 12345"
                  className="input-clinical"
                />
              </div>
              
              <div>
                <label className="label-clinical">Additional Notes (Optional)</label>
                <textarea
                  value={pharmacyData.notes}
                  onChange={(e) => setPharmacyData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Any special instructions for the pharmacy..."
                  className="input-clinical min-h-[80px] resize-none"
                  data-testid="pharmacy-notes-input"
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setPharmacyModalOpen(false);
                    setPharmacyData({
                      pharmacy_name: '',
                      pharmacy_email: '',
                      pharmacy_fax: '',
                      pharmacy_phone: '',
                      pharmacy_address: '',
                      notes: ''
                    });
                  }}
                  className="btn-outline flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendToPharmacy}
                  disabled={!pharmacyData.pharmacy_name || (!pharmacyData.pharmacy_email && !pharmacyData.pharmacy_fax) || sendingToPharmacy}
                  className="btn-teal flex-1 flex items-center justify-center gap-2"
                  data-testid="send-to-pharmacy-btn"
                >
                  {sendingToPharmacy ? (
                    <>
                      <div className="spinner-sm" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <PaperPlaneTilt size={18} />
                      Send Prescription
                    </>
                  )}
                </button>
              </div>
              
              <p className="text-xs text-slate-500 text-center">
                Prescription will be sent electronically to the pharmacy.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
